import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import API_BASE from '../services/apiBase';
import AuthSplitShell from '../components/AuthSplitShell';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const trimmed = email.trim();
    if (!trimmed) {
      setError('Email is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Request failed. Please try again.');
        return;
      }
      setSuccess(true);
    } catch {
      setError('Network error. Check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthSplitShell
      title="Forgot password"
      subtitle="Enter the email you use for your customer account. We'll send a link to reset your password if an account exists."
    >
      <motion.div
        className="auth-split-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        {success ? (
          <div className="auth-split-msg-success" style={{ marginBottom: 0 }}>
            <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ marginTop: 2 }} />
            <div>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Check your email</p>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>
                If an account exists for that address, we&apos;ve sent instructions. The link expires in one hour.
              </p>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', opacity: 0.9 }}>
                Didn&apos;t get it? Check spam or try again in a few minutes.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Email address</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`form-input ${error ? 'error' : ''}`}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                  <div style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}>
                    <Mail className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                {error && (
                  <span className="error-text" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </span>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn"
                style={{
                  width: '100%',
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
          </form>
        )}
      </motion.div>

      <motion.div
        className="auth-split-links"
        style={{ textAlign: 'center', marginTop: '1.5rem' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.4 }}
      >
        <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>
      </motion.div>
    </AuthSplitShell>
  );
};

export default ForgotPassword;
